import React, { useState } from 'react';
import { PageLoadingIndicator, PageLoadingSpinner } from '@kioskgaming/page-loading';
import { RefreshCw, BarChart2, Users, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { useDomains, useDomainAnalytics } from '../../../hooks/useApi';
import { useFormatDate } from '../../../hooks/useFormatDate';

const DomainAnalyticsTab: React.FC = () => {
  const fmt = useFormatDate();
  const { data: domainsData, isLoading: domainsLoading } = useDomains();
  const domains = domainsData?.data || [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const effectiveId = selectedId ?? (domains[0]?.id || null);
  const { data: analyticsData, isLoading: analyticsLoading, error, refetch } = useDomainAnalytics(effectiveId);
  const analytics = analyticsData?.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">Domain:</label>
          {domainsLoading ? (
            <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <PageLoadingSpinner size="sm" className="text-muted-foreground" />
              Loading...
            </span>
          ) : (
            <select
              value={effectiveId ?? ''}
              onChange={(e) => setSelectedId(e.target.value || null)}
              className="px-3 py-1.5 border rounded-md bg-background text-sm"
            >
              {domains.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.domain}{d.label ? ` (${d.label})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {analyticsLoading ? (
        <div className="flex h-48 items-center justify-center">
          <PageLoadingIndicator
            variant="section"
            spinnerClassName="text-muted-foreground"
            labelClassName="text-muted-foreground"
          />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-destructive">Failed to load analytics</p>
          </CardContent>
        </Card>
      ) : !analytics ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">Select a domain to view analytics.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Active Sessions (24h)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{analytics.activeSessions24h.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <BarChart2 className="h-4 w-4" />
                Total Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{analytics.totalSessions.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Last Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">
                {fmt(analytics.lastActivityAt)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DomainAnalyticsTab;
